import { useFormikContext } from "formik";
import React from "react";

import { BusinessIncomesBusinessCode44AD } from "../../../../config/filingConstants";
import { IBusinessIncomes } from "../../../../config/interfaces";
import { UtilService } from "../../../../services";
import PriceDisplay from "../../../PriceDisplay";
import { FormField, NumericInputField, SelectInputField, TextInputField } from "../../../fields";

const BPIRevenueMoreThan3CroresForm: React.FC = () => {
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<IBusinessIncomes>();

  return (
    <section className="mb-5">
      <FormField label="Name of Business" isRequired={true} sm={4}>
        <TextInputField
          name="BusinessName"
          value={values.BusinessName}
          onBlur={handleBlur("BusinessName")}
          onChange={(value) => {
            setFieldValue("BusinessName", value);
          }}
          isInvalid={Boolean(errors.BusinessName) && touched.BusinessName}
          errorMsg={errors.BusinessName}
        />
      </FormField>
      <FormField label="Business Code" isRequired={true} sm={4}>
        <SelectInputField
          placeholder="Business Code"
          name="BusinessCode"
          isSearchable={true}
          options={UtilService.convertToArray(BusinessIncomesBusinessCode44AD)}
          value={{
            "label": BusinessIncomesBusinessCode44AD[values.BusinessCode],
            "value": values.BusinessCode
          }}
          onChange={(selectedbusinessCode) => {
            if (selectedbusinessCode) {
              setFieldValue("BusinessCode", selectedbusinessCode.value);
            } else {
              setFieldValue("BusinessCode", undefined);
            }
          }}
          onBlur={handleBlur("BusinessCode")}
          isInvalid={Boolean(errors.BusinessCode) && touched.BusinessCode}
          errorMsg={errors.BusinessCode}
        />
      </FormField>
      <FormField label="Description" sm={4}>
        <TextInputField
          name="Description"
          value={values.Description}
          onBlur={(e) => {
            handleBlur(e);
            setFieldValue("Description", values.Description.trim());
          }}
          onChange={(value) => {
            setFieldValue("Description", value);
          }}
          isInvalid={Boolean(errors.Description) && touched.Description}
          errorMsg={errors.Description}
        />
      </FormField>
      <GrossTurnOverReceipt />
      <PresumptiveIncomes />
    </section>
  );
};

export default BPIRevenueMoreThan3CroresForm;

const GrossTurnOverReceipt: React.FC = () => {
  const getNetIncome = (value1: string, value2: string, value3: string) => {
    const gainsValue = Number(value1 || 0) + Number(value2 || 0) + Number(value3 || 0);
    const sixPrecElectronicMode = 0.06 * Number(value1);
    const eightPercentIncashOtherMode = 0.08 * (Number(value2) + Number(value3));
    const total = sixPrecElectronicMode + eightPercentIncashOtherMode;
    setFieldValue("GrossTurnover", gainsValue);
    setFieldValue("SixPerElectronicMode", sixPrecElectronicMode);
    setFieldValue("EightPerIncashOthermode", eightPercentIncashOtherMode);
    setFieldValue("Total", total);
  };
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<IBusinessIncomes>();
  return (
    <>
      <div className="os-title-wrap">
        <h5 className="fw-bold">
          1. Gross turnover or Receipts
          <span className="info-wrap" role="button"></span>
        </h5>
        <p className="leading-para mb-0">
          <PriceDisplay price={values?.GrossTurnover || "0"} />
        </p>
      </div>
      <hr />
      <section>
        <FormField
          label="a. Through a/c payee cheque or a/c payee bank draft or bank electronic clearing system received or prescribed electronic modes"
          sm={6}
          isRequired={true}
        >
          <NumericInputField
            decimalUpto={0}
            showLeftIcon={true}
            name="ElectronicMode"
            value={values?.ElectronicMode || ""}
            onChange={(value) => {
              setFieldValue("ElectronicMode", value);
              getNetIncome(value, values.InCash, values.OtherMode);
            }}
            onBlur={handleBlur("ElectronicMode")}
            isInvalid={Boolean(errors.ElectronicMode) && touched.ElectronicMode}
            errorMsg={errors.ElectronicMode}
          />
        </FormField>
        <FormField label="b. In Cash" sm={6} isRequired={true}>
          <NumericInputField
            decimalUpto={0}
            showLeftIcon={true}
            name="InCash"
            value={values?.InCash || ""}
            onChange={(value) => {
              setFieldValue("InCash", value);
              getNetIncome(values.ElectronicMode, value, values.OtherMode);
            }}
            onBlur={handleBlur("InCash")}
            isInvalid={Boolean(errors.InCash) && touched.InCash}
            errorMsg={errors.InCash}
          />
        </FormField>
        <FormField label="c. Any other modes" sm={6} isRequired={true}>
          <NumericInputField
            showLeftIcon={true}
            decimalUpto={0}
            name="OtherMode"
            value={values?.OtherMode || ""}
            onChange={(value) => {
              setFieldValue("OtherMode", value);
              getNetIncome(values.ElectronicMode, values.InCash, value);
            }}
            onBlur={handleBlur("OtherMode")}
            isInvalid={Boolean(errors.OtherMode) && touched.OtherMode}
            errorMsg={errors.OtherMode}
          />
        </FormField>
      </section>
    </>
  );
};

const PresumptiveIncomes: React.FC = () => {
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<IBusinessIncomes>();
  return (
    <>
      <div className="os-title-wrap">
        <h5 className="fw-bold">
          2. Presumptive Income u/s 44AD
          <span className="info-wrap" role="button"></span>
        </h5>
      </div>
      <hr />
      <section>
        <FormField label="a. 6% of (a)" sm={6}>
          <NumericInputField
            decimalUpto={0}
            isDisabled={true}
            showLeftIcon={true}
            name="SixPerElectronicMode"
            value={values?.SixPerElectronicMode || ""}
            onChange={(value) => {
              setFieldValue("SixPerElectronicMode", value);
            }}
            onBlur={handleBlur("SixPerElectronicMode")}
            isInvalid={Boolean(errors.SixPerElectronicMode) && touched.SixPerElectronicMode}
            errorMsg={errors.SixPerElectronicMode}
          />
        </FormField>
        <FormField label="b. 8% of (b+c)" sm={6}>
          <NumericInputField
            decimalUpto={0}
            isDisabled={true}
            showLeftIcon={true}
            name="EightPerIncashOthermode"
            value={values?.EightPerIncashOthermode || ""}
            onChange={(value) => {
              setFieldValue("EightPerIncashOthermode", value);
            }}
            onBlur={handleBlur("EightPerIncashOthermode")}
            isInvalid={Boolean(errors.EightPerIncashOthermode) && touched.EightPerIncashOthermode}
            errorMsg={errors.EightPerIncashOthermode}
          />
        </FormField>
        <div className="os-title-wrap">
          <h5 className="fw-bold">
            Total
            <span className="info-wrap" role="button"></span>
          </h5>
          <p className="leading-para mb-0">
            <PriceDisplay price={values.Total || "0"} />
          </p>
        </div>
      </section>
    </>
  );
};
